import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { sendFeedback } from '../api';
import NcIcon from './NcIcon';
import './FeedbackButton.css';

const TYPES = ['Ønske', 'Fejl', 'Spørgsmål'];
const MAX_LENGTH = 2000;
// What the text field suggests, per type — the three used to share one "Ønske" example.
const PLACEHOLDERS = {
  Ønske: 'Fx: Jeg kunne godt tænke mig at kunne filtrere på ejer.',
  Fejl: 'Fx: På Gennemgang viser listen 7 brugere uden MFA, men panelet viser 5.',
  Spørgsmål: 'Fx: Hvad betyder "Delvist understøttet" på Compliance?',
};

// "Feedback til NordComply": a button at the foot of every sidebar (all three portals share SidebarUserMenu) that
// opens a small form beside it. The mail goes to support@nordcomply.dk (see handleFeedback in the backend). The form
// stays open while the user moves around the app; it only closes with × or Escape.
export default function FeedbackButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(TYPES[0]);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const [anchor, setAnchor] = useState({ left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = btnRef.current?.closest('.sidebar')?.getBoundingClientRect();
      setAnchor({ left: (r ? r.right : 0) + 12 });
    };
    place();
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('resize', place);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle() {
    if (open) { setOpen(false); return; }
    if (status === 'sent') { setStatus('idle'); setMessage(''); }
    setEmail((e) => e || user?.email || '');
    // Placed before it first renders, so it does not jump in from the left edge.
    const r = btnRef.current?.closest('.sidebar')?.getBoundingClientRect();
    setAnchor({ left: (r ? r.right : 0) + 12 });
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    setError('');
    try {
      await sendFeedback({ type, message: message.trim(), email: email.trim() });
      setStatus('sent');
    } catch (err) {
      setError(err.message || 'Kunne ikke sende feedback');
      setStatus('error');
    }
  }

  return (
    <>
      <div className="fbk-wrap">
        <button type="button" ref={btnRef} className={`fbk-btn${open ? ' is-open' : ''}`} onClick={toggle} title="Feedback til NordComply" aria-haspopup="dialog" aria-expanded={open}>
          <NcIcon name="feedback" size={16} />
          <span className="fbk-btn__label">Feedback</span>
        </button>
      </div>

      {open && createPortal(
        <div className="fbk-pop" style={{ left: anchor.left }} role="dialog" aria-label="Feedback til NordComply">
          <button type="button" className="fbk-x" onClick={() => setOpen(false)} aria-label="Luk">×</button>
          {status === 'sent' ? (
            <div className="fbk-ok">
              <div className="fbk-ok__tick">✓</div>
              <b>Tak for din feedback</b>
              <span>Vi har modtaget den og vender tilbage, hvis vi har spørgsmål.</span>
              <button type="button" className="fbk-send" onClick={() => setOpen(false)}>Luk</button>
            </div>
          ) : (
            <form onSubmit={submit} className="fbk-form">
              <div>
                <h3>Feedback til NordComply</h3>
                <p>Skriv, hvad du savner, eller hvad der ikke virker. Vi læser alt.</p>
              </div>
              <div className="fbk-seg" role="radiogroup" aria-label="Type">
                {TYPES.map((t) => (
                  <button key={t} type="button" role="radio" aria-checked={type === t} className={type === t ? 'is-on' : ''} onClick={() => setType(t)}>{t}</button>
                ))}
              </div>
              <label>
                <span className="fbk-lab">Din besked</span>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={MAX_LENGTH} placeholder={PLACEHOLDERS[type]} autoFocus />
              </label>
              <label>
                <span className="fbk-lab">Svar sendes til</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              {status === 'error' && <p className="fbk-err">{error}</p>}
              <button type="submit" className="fbk-send" disabled={!message.trim() || status === 'sending'}>{status === 'sending' ? 'Sender…' : 'Send feedback'}</button>
            </form>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
